import type { ReactElement } from 'react'

import {
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import {
  ClientTimeZoneDateTime,
  DateTimeFormatType,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import { Colors, pxToRem, Typography } from '@b-stock/fe-core/design-system'

const ns = 'Listing.actionColumn.actionCard.offer'

const VARIANTS = {
  accepted: {
    accent: Colors.Semantic.Success.highlight,
    background: Colors.Semantic.Success.background,
    icon: faCircleCheck,
  },
  declined: {
    accent: Colors.Semantic.Error.highlight,
    background: Colors.Semantic.Error.background,
    icon: faCircleXmark,
  },
  pending: {
    accent: Colors.Semantic.Warning.highlight,
    background: Colors.Semantic.Warning.background,
    icon: faTriangleExclamation,
  },
} as const

export type OfferStatusAlertVariant = keyof typeof VARIANTS

const Alert = styled.div<{ $variant: OfferStatusAlertVariant }>`
  background: ${(o) => VARIANTS[o.$variant].background};
  border: ${pxToRem(1)} solid ${(o) => VARIANTS[o.$variant].accent};
  border-radius: 0.5rem;
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
`

const Icon = styled(FontAwesomeIcon)<{ $variant: OfferStatusAlertVariant }>`
  color: ${(o) => VARIANTS[o.$variant].accent};
  flex-shrink: 0;
  font-size: 0.875rem;
  margin-top: 0.125rem;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Title = styled.span`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.black};
`

const Subtitle = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const Note = styled.span`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.Neutral.black};
`

/**
 * Inset offer-decision alert — the offer card's analog of the auction top
 * StatusBanner, which does not apply to offers.
 */
export const OfferStatusAlert = ({
  amount,
  copyKey,
  date,
  isDecisionWindowNotOpen,
  variant,
}: {
  /** Omit to render the title alone. */
  amount?: ReactElement | string
  /** Names the copy under `status.*`; the variant only carries the color. */
  copyKey: string
  date?: Date | null
  isDecisionWindowNotOpen?: boolean
  variant: OfferStatusAlertVariant
}) => (
  <Alert $variant={variant} data-testid={`offer-status-alert-${copyKey}`}>
    <Icon $variant={variant} icon={VARIANTS[variant].icon} />
    <Content>
      <Title>
        <FormattedMessage id={`${ns}.status.${copyKey}.title`} />
      </Title>
      {amount && (
        <Subtitle>
          <FormattedMessage
            id={`${ns}.status.${copyKey}.subtitle`}
            values={{
              amount,
              date: date ? (
                <ClientTimeZoneDateTime
                  dateTime={date}
                  format={DateTimeFormatType.SHORT}
                />
              ) : (
                ''
              ),
            }}
          />
        </Subtitle>
      )}
      {isDecisionWindowNotOpen && (
        <Note>
          <FormattedMessage id={`${ns}.status.pending.decisionWindowNotOpen`} />
        </Note>
      )}
    </Content>
  </Alert>
)
